import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';

/**
 * SocketWorkerTimerService
 *
 * Provides a reliable interval timer using a Web Worker that is immune to
 * browser background-tab throttling. Browsers (especially Chrome) throttle
 * setInterval in background tabs to a minimum of 1 tick/minute, which breaks
 * heartbeat, reconnect, and health-check logic. Web Workers run on a separate
 * thread and are NOT subject to these throttling policies.
 *
 * Usage:
 *   - Call `start(intervalMs)` to begin emitting ticks on `tick$`.
 *   - Subscribe to `tick$` to receive timestamps at the requested interval.
 *   - Call `stop()` to tear down the worker and release resources.
 *
 * Falls back gracefully: if Web Workers are unavailable (e.g. restricted CSP),
 * the caller should maintain its own setInterval as a fallback.
 */
@Injectable({ providedIn: 'root' })
export class SocketWorkerTimerService implements OnDestroy {
  private worker: Worker | null = null;
  private blobUrl: string | null = null;
  private fallbackTimer: ReturnType<typeof setInterval> | null = null;
  private _isRunning = false;
  private _usingWorker = false;
  private _intervalMs = 1000;

  /** Emits a timestamp (Date.now()) on every tick. */
  public readonly tick$ = new Subject<number>();

  /**
   * Start the timer at the given interval.
   * Tries Web Worker first (immune to background-tab throttling).
   * Falls back to main-thread setInterval if Workers are unavailable.
   * Always emits on `tick$` regardless of which strategy is used.
   *
   * @param intervalMs Tick interval in milliseconds (default: 1000)
   */
  start(intervalMs: number = 1000): void {
    this.stop();
    this._intervalMs = intervalMs;

    if (this._tryStartWorker(intervalMs)) {
      this._usingWorker = true;
      this._isRunning = true;
      return;
    }

    // Fallback: main-thread setInterval (will be throttled in background tabs)
    this._startFallbackTimer(intervalMs);
    this._usingWorker = false;
    this._isRunning = true;
  }

  /** Stop the timer and release all resources. */
  stop(): void {
    this._teardownWorker();
    this._clearFallbackTimer();
    this._isRunning = false;
    this._usingWorker = false;
  }

  /** Whether the timer is currently running and emitting ticks. */
  get isRunning(): boolean {
    return this._isRunning;
  }

  /** Whether the timer is using a Web Worker (true) or main-thread fallback (false). */
  get usingWorker(): boolean {
    return this._usingWorker;
  }

  ngOnDestroy(): void {
    this.stop();
    this.tick$.complete();
  }

  // --- Private: Worker strategy ---

  private _tryStartWorker(intervalMs: number): boolean {
    if (typeof Worker === 'undefined') {
      return false;
    }

    try {
      const workerCode = `
        let interval = null;
        self.onmessage = function(e) {
          if (e.data.command === 'start') {
            if (interval) clearInterval(interval);
            interval = setInterval(function() {
              self.postMessage({ type: 'tick', timestamp: Date.now() });
            }, e.data.interval);
          } else if (e.data.command === 'stop') {
            if (interval) { clearInterval(interval); interval = null; }
          }
        };
      `;

      const blob = new Blob([workerCode], { type: 'application/javascript' });
      this.blobUrl = URL.createObjectURL(blob);
      this.worker = new Worker(this.blobUrl);

      this.worker.onmessage = (e: MessageEvent) => {
        if (e.data?.type === 'tick') {
          this.tick$.next(e.data.timestamp);
        }
      };

      this.worker.onerror = (err: ErrorEvent) => {
        // Worker crashed at runtime — teardown and fall back to setInterval.
        console.error('[SocketWorkerTimer]: Worker error, switching to fallback timer', err.message);
        this._teardownWorker();
        this._usingWorker = false;
        this._startFallbackTimer(this._intervalMs);
      };

      this.worker.postMessage({ command: 'start', interval: intervalMs });
      return true;
    } catch {
      // Blob URL or Worker constructor failed (e.g. strict CSP)
      this._teardownWorker();
      return false;
    }
  }

  // --- Private: Fallback strategy ---

  private _startFallbackTimer(intervalMs: number): void {
    this._clearFallbackTimer();
    this.fallbackTimer = setInterval(() => {
      this.tick$.next(Date.now());
    }, intervalMs);
  }

  private _clearFallbackTimer(): void {
    if (this.fallbackTimer !== null) {
      clearInterval(this.fallbackTimer);
      this.fallbackTimer = null;
    }
  }

  // --- Private: Worker cleanup ---

  private _teardownWorker(): void {
    if (this.worker) {
      try { this.worker.postMessage({ command: 'stop' }); } catch { /* ignored */ }
      try { this.worker.terminate(); } catch { /* ignored */ }
      this.worker = null;
    }
    if (this.blobUrl) {
      try { URL.revokeObjectURL(this.blobUrl); } catch { /* ignored */ }
      this.blobUrl = null;
    }
  }
}
